import { type InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/http';
import { useInfiniteList } from '@/lib/pagination/use-infinite-list';
import {
  dispatchTransfers,
  fetchCareBoard,
  fetchCareHistory,
  fetchCareLoads,
  fetchCareMoney,
  fetchCaseCareCards,
  fetchDisruptionTrips,
  fetchRunManifest,
  fetchTransferBoard,
  planTransfers,
  postAddRun,
  fetchCareLookup,
  postCareOutcome,
  postCareRedeem,
  postCareTopUp,
  postCareVoid,
  postMoveSeats,
  postOperatorCheckIn,
  postPassengerCondition,
  postResponsibleAdult,
  postTransportOutcome,
  transferRunAction,
  triggerCheckinSweep,
  triggerDispatchCars,
} from './api';
import type {
  AddRunInput,
  AddRunResult,
  CareBoard,
  CareCardHistory,
  CareLoads,
  CareMoney,
  CareLookupResult,
  CareOutcomeKind,
  CareRedeemResult,
  CareTopUpInput,
  CareTopUpResult,
  CaseCareCard,
  CheckinReminderSweepResult,
  DispatchCarsResult,
  DispatchTransfersInput,
  DispatchTransfersResult,
  DisruptionTripsPage,
  ITransferBoard,
  MoveSeatsInput,
  MoveSeatsResult,
  OperatorCheckInResult,
  OperatorLinkResponsibleAdultResult,
  OperatorSetConditionResult,
  PassengerCondition,
  PlanTransfersInput,
  RunManifest,
  TransferRunAction,
  TransportOutcomeInput,
  TransportOutcomeResult,
  IDisruptionTotalSpend,
} from './types';
import {
  cancelSelection,
  getRebookingOptions,
  issueTicket,
  overrideSelection,
  rejectOption,
  selectOption,
  setPassengerAges,
  triggerGroupGeneration,
} from '@/lib/rebooking/api';
import type {
  GenerationRunAccepted,
  PassengerAge,
  PassengerAgesResult,
  OverrideRequest,
  RebookingGroup,
  TicketResult,
} from '@/lib/rebooking/types';
import {
  containDisruption,
  declareAccommodationOutcome,
  excludePnrFromCase,
  fetchDisruption,
  fetchDisruptionPriceBreakdown,
  fetchDisruptionSpend,
  fetchDisruptionProgress,
  fetchDisruptionRosterPage,
  fetchDisruptionsOverview,
  fetchDisruptionsPage,
  fetchOperatedAirports,
  fetchRebookingDetail,
  fetchResolutionState,
  fetchRosterSummary,
  issueGroupVoucher,
  triggerAllocate,
  triggerAutoRebook,
  triggerIssueVouchers,
  triggerResolveNext,
  issueDisruptionTickets,
  sendDisruptionOffers,
  forceAcceptAllDisruption,
  type AutoRebookArgs,
  type BulkAutoRebookResultV2,
} from './api';
import type { PnrGroupScope } from '@/lib/pnr/scope';
import type {
  AirportOption,
  BulkAllocateResult,
  DisruptionPriceBreakdown,
  DisruptionDetail,
  DisruptionFilters,
  DisruptionOverview,
  DisruptionPnrGroupRow,
  DisruptionProgress,
  DisruptionRosterFilters,
  IssueGroupVoucherResult,
  IssueVouchersResult,
  RebookingDetailView,
  ResolutionState,
  ResolveNextResult,
  ResolutionOp,
  RosterSummary,
} from './types';

// Disruptions data hooks per docs/ui/operator-ui-tds.md §6 (data layer) +
// docs/ui/frontend-conventions.md §7.5.1 (infinite-scroll list). URN-shaped,
// hierarchical query keys so partial invalidation works.

/** Cursor-paginated list + infinite scroll (filters are the query-key tail). */
export function useDisruptionsList(filters: DisruptionFilters, enabled = true) {
  return useInfiniteList<import('./types').DisruptionListRow, DisruptionFilters>({
    baseKey: ['disruptions'],
    filters,
    queryFn: (f, cursor) => fetchDisruptionsPage({ ...f, ...(cursor ? { cursor } : {}) }),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * The network-situation board payload (map + KPI band + priority queue all read
 * this ONE query). Kept fresh by WS pushes — a disruption-progress event for any
 * joined row invalidates `['disruptions','overview']` (apply-event.ts); there is
 * no poll.
 */
export function useDisruptionsOverview(enabled = true) {
  return useQuery<DisruptionOverview>({
    queryKey: ['disruptions', 'overview'],
    queryFn: fetchDisruptionsOverview,
    enabled,
    staleTime: 15_000,
  });
}

/** One disruption detail. */
export function useDisruption(urn: string | undefined) {
  return useQuery<DisruptionDetail>({
    queryKey: ['disruptions', urn],
    queryFn: () => fetchDisruption(urn!),
    enabled: !!urn,
    staleTime: 30_000,
  });
}

/** Distinct list of airports the tenant operates from — drives the airport pickers. */
export function useOperatedAirports(enabled = true) {
  return useQuery<AirportOption[]>({
    queryKey: ['disruptions', 'airports'],
    queryFn: fetchOperatedAirports,
    enabled,
    staleTime: 5 * 60_000, // cached server-side too; client cache matches.
  });
}

/* ── S6 (NEX-281) — disruption-wide bulk-command triggers ─────────────────────── */

/**
 * Bulk auto-rebook trigger ("Generate all options"). One BFF call; the domain
 * fans out. NEX-293: no progress invalidation here — the BFF records the run and
 * pushes the new run-state over the disruption WS room on the same request, so
 * the Progress tab + bulk bar reflect it live (applyEvent → setQueryData) without
 * a refetch.
 */
export function useTriggerAutoRebook(urn: string) {
  return useMutation<BulkAutoRebookResultV2, Error, AutoRebookArgs | void>({
    mutationFn: (args) => triggerAutoRebook(urn, args ?? {}),
  });
}

/** Bulk hotel-allocation trigger ("Allocate hotels"). One BFF call; domain fans out.
 * Run-state lands live over the WS (NEX-293) — no progress invalidation needed. */
export function useTriggerAllocate(urn: string) {
  return useMutation<BulkAllocateResult, Error, void>({
    mutationFn: () => triggerAllocate(urn),
  });
}

/**
 * Bulk meal-voucher issuance ("Issue meal vouchers") — the meal counterpart of
 * "Allocate hotels". One BFF call fans out wallet card-issues; each emits card-issued
 * → cases flips the roster chip to "Voucher issued". That flip is an ASYNC Kafka
 * projection (~1–5 s), so we invalidate the roster on success AND once more after a
 * short delay to pick up the denormalized chips without forcing the operator to refresh.
 */
export function useTriggerIssueVouchers(urn: string) {
  const qc = useQueryClient();
  return useMutation<IssueVouchersResult, Error, void>({
    mutationFn: () => triggerIssueVouchers(urn),
    onSuccess: () => {
      const invalidate = (): void => {
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'progress'] });
      };
      invalidate();
      setTimeout(invalidate, 4000);
    },
  });
}

/**
 * Allocate a hotel for ONE PNR (sub-case) from the roster PNR drawer. Scopes the run
 * to that PNR via `subCaseUrns` — a macro case holds MANY PNRs, so passing only the
 * case booked a hotel for every PNR in it (NOT what the operator asked when clicking
 * "Allocate hotel" on a single PNR). The `caseUrn` still goes through so booking
 * resolves the case's airport/airline context; hotels are still calculated for the
 * airport. The reservation badge flips live over the case WS room; we nudge the
 * roster + progress rollups so the disruption counts reconcile.
 */
export function useAllocateCaseHotel(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    BulkAllocateResult,
    Error,
    // `disruptionUrn` ROUTES the allocate (the bulk endpoint is disruption-scoped)
    // — the scope's URN at disruption scope, the row's parent at case scope.
    { caseUrn: string; subCaseUrn: string; disruptionUrn: string }
  >({
    mutationFn: ({ caseUrn, subCaseUrn, disruptionUrn }) =>
      triggerAllocate(disruptionUrn, [caseUrn], [subCaseUrn]),
    onSuccess: () => {
      // The reservation badge flips live over the case WS room; we nudge the
      // active scope's roster (+ disruption progress) so the counts reconcile.
      if (scope.kind === 'disruption') {
        void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'roster'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'progress'] });
      } else {
        void qc.invalidateQueries({ queryKey: ['cases', scope.caseUrn, 'pnr-groups'] });
      }
    },
  });
}

/**
 * Issue the meal voucher for ONE PNR (sub-case) from the roster drawer's
 * MealVoucherCard — the per-PNR counterpart of `useTriggerIssueVouchers`. One BFF
 * call issues a single wallet card for this group; wallet emits card-issued → cases
 * flips the voucher to ISSUED. That flip is an ASYNC Kafka projection (~1–5 s), so we
 * invalidate the roster + progress + the case's reservations on success AND once more
 * after a short delay to pick up the "Voucher issued" chip without a manual refresh.
 */
export function useIssueGroupVoucher(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    IssueGroupVoucherResult,
    Error,
    // `disruptionUrn` ROUTES the voucher issue (disruption-scoped endpoint);
    // OMIT it for a standalone case — the issue then routes case-scoped.
    // `discretionary` switches from the engine's entitlement to an
    // operator-chosen amount (which the BFF then requires a reason for).
    {
      subCaseUrn: string;
      caseUrn: string;
      disruptionUrn?: string;
      discretionary?: { amountUsd: number; reason: string };
    }
  >({
    mutationFn: ({ subCaseUrn, caseUrn, disruptionUrn, discretionary }) =>
      issueGroupVoucher(disruptionUrn, subCaseUrn, caseUrn, discretionary),
    onSuccess: (_data, { caseUrn }) => {
      const invalidate = (): void => {
        if (scope.kind === 'disruption') {
          void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'roster'] });
          void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'progress'] });
        } else {
          void qc.invalidateQueries({ queryKey: ['cases', scope.caseUrn, 'pnr-groups'] });
        }
        void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'reservations'] });
        // Standalone-case card read — the freshly issued card must show up in
        // the drawer without a reload.
        void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'care-cards'] });
      };
      invalidate();
      setTimeout(invalidate, 4000);
    },
  });
}

/**
 * The wallet cards of a STANDALONE case (no parent disruption ⇒ no care board).
 * Enabled only when a caseUrn is given — disruption-scoped surfaces keep
 * reading the care board and never fire this.
 */
export function useCaseCareCards(caseUrn?: string) {
  return useQuery<CaseCareCard[]>({
    queryKey: ['cases', caseUrn, 'care-cards'],
    queryFn: () => fetchCaseCareCards(caseUrn!),
    enabled: !!caseUrn,
  });
}

/* ── S7 (NEX-282) — control-center data hooks ────────────────────────────────── */

/**
 * Disruption-wide roster — grouped by PNR (S12), cursor-paginated + infinite-
 * scrolled (frontend-conventions §7.5.1). Each item is a PNR group (the operator's
 * actionable unit); its member passengers ride nested under it. The priority/
 * rebooking-status/search filters are pushed to the SERVER (the query-key tail) and
 * operate at the group level, so a filter change starts a fresh walk over the
 * filtered set rather than post-filtering loaded pages.
 */
export function useDisruptionRoster(
  urn: string | undefined,
  filters: DisruptionRosterFilters,
  enabled = true,
) {
  return useInfiniteList<DisruptionPnrGroupRow, DisruptionRosterFilters>({
    baseKey: ['disruptions', urn, 'roster'],
    filters,
    queryFn: (f, cursor) =>
      fetchDisruptionRosterPage(urn!, { ...f, ...(cursor ? { cursor } : {}) }),
    staleTime: 15_000,
    enabled: enabled && !!urn,
  });
}

/**
 * The 4 MVP header tiles for the disruption Passengers roster (U1) —
 * affected-flights / passengers / connections-at-risk / estimated-cost, composed
 * server-side by the operator-bff roster-summary aggregate (B1).
 *
 * Realtime = WS, NO polling. The key is nested UNDER the roster prefix
 * (`['disruptions', urn, 'roster', 'summary']`) precisely so the existing live
 * `DisruptionProgressChanged` handler — which invalidates
 * `['disruptions', urn, 'roster']` (a prefix match) on a terminal/settle push
 * (apply-event.ts) — refetches these tiles the moment the run mutates the rollup,
 * with no interval poll here. A run in progress therefore climbs the tiles
 * live over the disruption WS room the detail page already joined.
 */
export function useRosterSummary(urn: string | undefined, enabled = true) {
  return useQuery<RosterSummary>({
    queryKey: ['disruptions', urn, 'roster', 'summary'],
    queryFn: () => fetchRosterSummary(urn!),
    enabled: !!urn && enabled,
    staleTime: 15_000,
  });
}

/**
 * Live rebooking-progress + run-state rollup. NEX-293: NO polling. The control
 * center subscribes to the disruption's Socket.IO room (joinDisruption, in the
 * detail route) and the BFF pushes a fresh `DisruptionProgressChanged` snapshot
 * whenever the counters or the bulk `activeRun` change; applyEvent writes it into
 * THIS query's cache via setQueryData (frontend-conventions §15 — never refetch
 * on a WS event). This hook does exactly ONE initial HTTP read on mount to seed
 * the cache, then defers to the live push (no interval poll). The Progress
 * tab and the bulk bar both read this single cache key, so a push updates both.
 */
export function useDisruptionProgress(urn: string | undefined, enabled = true) {
  return useQuery<DisruptionProgress>({
    queryKey: ['disruptions', urn, 'progress'],
    queryFn: () => fetchDisruptionProgress(urn!),
    enabled: !!urn && enabled,
    // One-shot read on open; live updates arrive over the WS (setQueryData). No
    // interval poll — the WS push is the source of truth thereafter.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * The Overview resolution-cockpit read-aggregate (disruption-overview / U1) —
 * KPIs + progress + step checklist + problems/implications + the recommended next
 * step, composed server-side by the operator-bff `resolution-state@v1` aggregate
 * (B1).
 *
 * Realtime = WS, NO polling. This does exactly ONE HTTP read on mount to seed the
 * cockpit (staleTime Infinity, no interval poll). Live movement then arrives
 * over the disruption Socket.IO room the detail route already joined: the existing
 * `DisruptionProgressChanged` handler (apply-event.ts) writes each fresh snapshot
 * into the WS-backed `['disruptions', urn, 'progress']` cache (setQueryData, no
 * refetch). The cockpit reads that same progress cache and invalidates THIS
 * aggregate when the run snapshot moves — so the read re-runs off the WS push, NOT
 * a client timer.
 */
export function useResolutionState(urn: string | undefined, enabled = true) {
  return useQuery<ResolutionState>({
    queryKey: ['disruptions', urn, 'resolution-state'],
    queryFn: () => fetchResolutionState(urn!),
    enabled: !!urn && enabled,
    // One-shot read on open; live updates arrive over the WS invalidation. No
    // interval poll — the WS push is the source of truth thereafter.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * One-click "Resolver" trigger (disruption-overview / U3) — fires the ONE
 * recommended next resolution op via the FROZEN `resolve-next@v1` seam (B2). This
 * is a MUTATION (a POST), not a query: the caller runs it TWICE per step — first
 * with `{ dryRun: true }` for the implications preview, then `{ dryRun: false }` to
 * fire the confirmed step. On a real (non-dryRun) fire we invalidate the
 * resolution-state aggregate so the cockpit re-reads the advanced step / next
 * recommendation; the LIVE per-step progress itself arrives over the disruption WS
 * room (the sibling activity panel), so there is NO interval poll / refetch loop
 * here. A `dryRun` preview mutates nothing, so it does not invalidate.
 */
export function useResolveNext(urn: string) {
  const qc = useQueryClient();
  return useMutation<ResolveNextResult, Error, { dryRun?: boolean; expectedOp?: ResolutionOp } | void>({
    mutationFn: (args) => triggerResolveNext(urn, args ?? {}),
    onSuccess: (_result, args) => {
      // Only a real fire advances the sequence — re-read the aggregate so the
      // cockpit reflects the newly-running step + the next recommendation. The
      // dryRun preview commits nothing, so it leaves the cache untouched.
      if (!args || args.dryRun !== true) {
        void qc.invalidateQueries({
          queryKey: ['disruptions', urn, 'resolution-state'],
        });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'progress'] });
      }
    },
  });
}

/** Disruption-wide ticketing (guided step 2) — invalidates roster/progress/state on settle. */
export function useIssueDisruptionTickets(urn: string) {
  const qc = useQueryClient();
  return useMutation<{ disruptionUrn: string; tally: Record<string, number> }, Error, void>({
    mutationFn: () => issueDisruptionTickets(urn),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn] });
    },
  });
}

/** Disruption-wide "Send offers" REPAIR (QA fix 4, supervisor) — re-opens the
 *  acceptance gate for complete offers a missed event left behind. */
export function useSendDisruptionOffers(urn: string) {
  const qc = useQueryClient();
  return useMutation<{ disruptionUrn: string; tally: Record<string, number> }, Error, void>({
    mutationFn: () => sendDisruptionOffers(urn),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn] });
    },
  });
}

/** Disruption-wide force-accept (guided step 5, supervisor). */
export function useForceAcceptAllDisruption(urn: string) {
  const qc = useQueryClient();
  // `subCaseUrns` = the selective accept (Operar's «Aceptar por ellos…»); none = all.
  return useMutation<{ disruptionUrn: string; tally: Record<string, number> }, Error, readonly string[] | void>({
    mutationFn: (subCaseUrns) => forceAcceptAllDisruption(urn, subCaseUrns || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn] });
    },
  });
}

/**
 * Rich per-PNR rebooking DETAIL for the progress dialog (NEX-364 / U1) — the
 * one-shot open read that SEEDS the score components / strategies / original
 * itinerary. Like `useDisruptionProgress`, this does exactly ONE HTTP read on
 * open (staleTime Infinity, no interval poll); live per-PNR settle outcomes then
 * arrive over the WS progress feed and are merged into THIS cache by applyEvent
 * (`mergeSettleOutcomesIntoDetail`) — never a refetch (frontend-conventions §15).
 */
export function useRebookingDetail(urn: string | undefined, enabled = true) {
  return useQuery<RebookingDetailView>({
    queryKey: ['disruptions', urn, 'rebooking-detail'],
    queryFn: () => fetchRebookingDetail(urn!),
    enabled: !!urn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Price headline + per-flight breakdown for the price tab (`airline-money@v2`; the
 *  query key keeps the route's `cost-breakdown` path). */
export function useDisruptionPriceBreakdown(urn: string | undefined, enabled = true) {
  return useQuery<DisruptionPriceBreakdown>({
    queryKey: ['disruptions', urn, 'cost-breakdown'],
    queryFn: () => fetchDisruptionPriceBreakdown(urn!),
    enabled: !!urn && enabled,
    staleTime: 15_000,
  });
}

/** What the disruption cost — every service, net (`costs.view` only; Operar's cost tile). */
export function useDisruptionSpend(urn: string | undefined, enabled = true) {
  return useQuery<IDisruptionTotalSpend>({
    queryKey: ['disruptions', urn, 'spend'],
    queryFn: () => fetchDisruptionSpend(urn!),
    enabled: !!urn && enabled,
    staleTime: 15_000,
  });
}

/** Contain a disruption (DETECTED→CONTAINED §3.2); refreshes detail + list. */
/** What a contain may carry: a note, and — when the caller knows it — the mode. */
export interface IContainInput {
  note?: string;
  mode?: 'NATURAL' | 'FORCED';
}

export function useContainDisruption(urn: string) {
  const qc = useQueryClient();
  return useMutation({
    // A bare string is the note (the Tower / classic callers); Operar sends the mode too.
    mutationFn: (input?: string | IContainInput) =>
      typeof input === 'string' || input === undefined
        ? containDisruption(urn, input)
        : containDisruption(urn, input.note, input.mode),
    onSuccess: (detail) => {
      qc.setQueryData(['disruptions', urn], detail);
      // Match the list query key (`['disruptions', <filters>]`) by invalidating
      // the `['disruptions']` prefix — `['disruptions', 'list']` never matched.
      void qc.invalidateQueries({ queryKey: ['disruptions'] });
    },
    // A refusal (409) is a real answer from the domain rule — the state may also
    // have moved (someone issued a ticket); re-read the aggregate either way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
    },
  });
}

/* ── S8 (NEX-283) — per-PNR-group actions from the roster drawer ─────────────── */

/**
 * After a per-group write (select / override / exclude) from the roster drawer,
 * the group's row + the progress rollup must reflect it. We invalidate the
 * disruption's roster walk (`['disruptions', urn, 'roster']` — `useDisruptionRoster`'s
 * baseKey) so the changed row re-reads its rebooking status, and the progress
 * query so the S5 poll reconciles immediately. The case's own rebooking view is
 * invalidated too, so opening the case later isn't stale.
 */
function invalidateGroupWrite(
  qc: ReturnType<typeof useQueryClient>,
  scope: PnrGroupScope,
  caseUrn: string,
): void {
  // The roster slice for the active scope — the disruption control-center roster
  // OR the case-detail PNR-group roster. Both render the same row shape.
  const rosterKey =
    scope.kind === 'disruption'
      ? ['disruptions', scope.disruptionUrn, 'roster']
      : ['cases', scope.caseUrn, 'pnr-groups'];
  void qc.invalidateQueries({ queryKey: rosterKey });
  if (scope.kind === 'disruption') {
    void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'progress'] });
  }
  // The per-group drawer query is keyed ['disruptions','group-options',caseUrn,subCaseUrn]
  // (NOT prefixed by disruptionUrn) — this prefix-matches it so the drawer refetches
  // after a select / issue-ticket and reflects the new state (e.g. "Ticketed"). The
  // drawer reads rebooking DIRECTLY, so this immediate refetch is already correct.
  void qc.invalidateQueries({ queryKey: ['disruptions', 'group-options', caseUrn] });
  void qc.invalidateQueries({ queryKey: ['cases', caseUrn, 'rebooking'] });
  // The ROSTER GRID instead shows selectedOptionUrn + the assigned flight that cases
  // denormalises onto the sub-case ASYNC (rebooking → bus → cases), landing ~1–4s
  // after the write. The immediate invalidate above therefore refetches the grid
  // BEFORE the new flight is there — it keeps showing the OLD one. Re-fetch the
  // roster a couple of times over the next few seconds to pick up the denorm.
  // Bounded + one-shot (NOT a poll). Proper fix: a post-denorm CaseChanged WS push
  // from cases (like the hotel denorm already does) so this can be removed.
  for (const ms of [2500, 5000]) {
    setTimeout(() => void qc.invalidateQueries({ queryKey: rosterKey }), ms);
  }
}

/**
 * The rebooking options for ONE PNR group, drilled out of the case's options view
 * (the BFF returns ALL the case's groups; we pick the one whose `groupId` is this
 * sub-case URN). Drives the drawer's selectable option list. Only enabled once the
 * drawer is open AND the group reportedly has options (`optionsCount > 0`); a group
 * with no options yet shows the empty/disabled state without a fetch.
 */
export function useDisruptionGroupOptions(
  caseUrn: string | undefined,
  subCaseUrn: string | undefined,
  enabled = true,
) {
  return useQuery<RebookingGroup | null>({
    queryKey: ['disruptions', 'group-options', caseUrn, subCaseUrn],
    queryFn: async () => {
      const view = await getRebookingOptions(caseUrn!);
      return view.groups.find((g) => g.groupId === subCaseUrn) ?? null;
    },
    enabled: !!caseUrn && !!subCaseUrn && enabled,
    staleTime: 15_000,
  });
}

/** Select a rebooking option for a PNR group from the roster drawer (§8). */
export function useSelectGroupOption(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    RebookingGroup,
    Error,
    { subCaseUrn: string; caseUrn: string; optionUrn: string; seats?: number }
  >({
    mutationFn: (input) =>
      selectOption(
        input.subCaseUrn,
        { optionUrn: input.optionUrn, ...(input.seats ? { seats: input.seats } : {}) },
        input.caseUrn
      ),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** Operator-gated mock PSS ticketing for a SELECTED PNR group (rebooking-engine
 *  S5). Explicit operator action from the drawer — never auto-fired. */
type RosterPage = { items: DisruptionPnrGroupRow[]; nextCursor?: string | null };

/** Optimistically stamp the issued ticket on the grid roster cache. The roster is
 *  denormalised from `cases` ASYNC (rebooking → event bus → cases), so an immediate
 *  refetch would miss the just-issued ticket and the badge would lag until a manual
 *  refresh. This reflects it now; the eventual denorm is consistent. */
function patchRosterTicket(
  data: InfiniteData<RosterPage> | undefined,
  subCaseUrn: string,
  ticketNumber: string,
): InfiniteData<RosterPage> | undefined {
  // The roster prefix also holds non-paged entries (desk-walk, summary: `{ rows,
  // truncated }`) — `setQueriesData` hands those here too; leave them untouched.
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((g) =>
        g.subCaseUrn === subCaseUrn ? { ...g, ticketNumber } : g,
      ),
    })),
  };
}

export function useIssueTicket(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<TicketResult, Error, { subCaseUrn: string; caseUrn: string }>({
    mutationFn: (input) => issueTicket(input.subCaseUrn, input.caseUrn),
    onSuccess: (data, input) => {
      // Grid: optimistic (the roster denorm is async). Do NOT invalidate the roster
      // here — that refetches the not-yet-denormalised row and reverts the badge.
      // Patch the active scope's roster slice (disruption roster OR case pnr-groups
      // — both are InfiniteData<RosterPage> of the same row shape).
      const rosterKey =
        scope.kind === 'disruption'
          ? ['disruptions', scope.disruptionUrn, 'roster']
          : ['cases', scope.caseUrn, 'pnr-groups'];
      qc.setQueriesData<InfiniteData<RosterPage>>(
        { queryKey: rosterKey },
        (old) => patchRosterTicket(old, input.subCaseUrn, data.ticketNumber),
      );
      // Drawer: the group view is synchronous (the domain persisted the ticket) —
      // refetch it so the modal flips to "Ticketed".
      void qc.invalidateQueries({ queryKey: ['disruptions', 'group-options', input.caseUrn] });
    },
  });
}

/** Force a manual rebooking option for a PNR group from the roster drawer (§8). */
export function useOverrideGroupOption(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    RebookingGroup,
    Error,
    { subCaseUrn: string; caseUrn: string } & OverrideRequest
  >({
    mutationFn: (input) =>
      overrideSelection(
        input.subCaseUrn,
        {
          flightNumber: input.flightNumber,
          departureAt: input.departureAt,
          cabin: input.cabin,
          reason: input.reason,
          // POL-14: carry the override intent + waived rule so the BFF can permission-gate
          // a policy-rule waiver and the domain audit records what was waived.
          ...(input.overrideType ? { overrideType: input.overrideType } : {}),
          ...(input.waivedRule ? { waivedRule: input.waivedRule } : {}),
          // Vendor validation inputs (§10.4): route + party size.
          ...(input.origin ? { origin: input.origin } : {}),
          ...(input.destination ? { destination: input.destination } : {}),
          ...(input.seats ? { seats: input.seats } : {}),
          // The picker's provider offer — the domain materialises THAT offer.
          ...(input.offerRef ? { offerRef: input.offerRef } : {}),
        },
        input.caseUrn,
      ),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** Exclude a PNR group (sub-case) from its case via the roster drawer (§8). */
/** Declare a no-hotel outcome for one PNR (hotel desk ladder ③). Invalidates the
 *  roster so the desk drops the PNR from the queue immediately. */
export function useDeclareAccommodationOutcome(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    { declared: true; subCaseUrn: string; outcome: string },
    Error,
    {
      subCaseUrn: string;
      caseUrn: string;
      disruptionUrn: string;
      outcome: 'SELF_MANAGED' | 'NO_ACCOMMODATION';
      note?: string;
    }
  >({
    mutationFn: ({ subCaseUrn, caseUrn, disruptionUrn, outcome, note }) =>
      declareAccommodationOutcome(disruptionUrn, subCaseUrn, {
        caseUrn,
        outcome,
        ...(note ? { note } : {}),
      }),
    onSuccess: () => {
      if (scope.kind === 'disruption') {
        void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'roster'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', scope.disruptionUrn, 'progress'] });
      } else {
        void qc.invalidateQueries({ queryKey: ['cases', scope.caseUrn, 'pnr-groups'] });
      }
    },
  });
}

export function useExcludePnr(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    { excluded: true; subCaseUrn: string },
    Error,
    // `disruptionUrn` ROUTES the action — the scope's URN at disruption scope, the
    // row's parent disruption at case scope (`routingDisruptionUrn`).
    { subCaseUrn: string; caseUrn: string; disruptionUrn: string }
  >({
    mutationFn: (input) => excludePnrFromCase(input.disruptionUrn, input.subCaseUrn, input.caseUrn),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** Generate options for ONE PNR group from the drawer — the operator resolves a
 *  single passenger WITHOUT the disruption-wide bulk. Generation is async (the
 *  domain projects options onto the group via the bus); the drawer's options query
 *  is invalidated so it re-reads as they land. */
export function useGenerateGroupOptions(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    GenerationRunAccepted,
    Error,
    // `force` (POL-16): supervisor-forced generation past a disabling policy —
    // the BFF gates the role and requires the reason.
    { subCaseUrn: string; caseUrn: string; force?: { reason: string } }
  >({
    mutationFn: (input) => triggerGroupGeneration(input.caseUrn, input.subCaseUrn, input.force),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** «Cargar edades» — save a group's children / infants' ages; the domain regenerates its
 *  options with them, so the roster and the open card refetch like after a generate. */
export function useSetPassengerAges(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<PassengerAgesResult, Error, { subCaseUrn: string; caseUrn: string; ages: PassengerAge[] }>({
    mutationFn: (input) => setPassengerAges(input.caseUrn, input.subCaseUrn, input.ages),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** Cancel a group's SELECTED option (SELECTED→OVERRIDDEN, §10.4) so the operator can
 *  re-decide. Reason is mandatory (audited). */
export function useCancelGroupSelection(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    RebookingGroup,
    Error,
    { subCaseUrn: string; caseUrn: string; reason: string }
  >({
    mutationFn: (input) => cancelSelection(input.subCaseUrn, { reason: input.reason }, input.caseUrn),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/** Reject all of a group's options (OPTIONS_READY→NO_OPTIONS) — none are acceptable,
 *  escalate / regenerate. The BFF requires an `optionUrn` on the reject body (the
 *  domain rejects the whole set regardless), so the caller passes a representative
 *  option URN. */
export function useRejectGroupOptions(scope: PnrGroupScope) {
  const qc = useQueryClient();
  return useMutation<
    RebookingGroup,
    Error,
    { subCaseUrn: string; caseUrn: string; optionUrn: string; reason?: string }
  >({
    mutationFn: (input) =>
      rejectOption(
        input.subCaseUrn,
        { optionUrn: input.optionUrn, ...(input.reason ? { reason: input.reason } : {}) },
        input.caseUrn,
      ),
    onSuccess: (_data, input) => invalidateGroupWrite(qc, scope, input.caseUrn),
  });
}

/* ── rebooking-progress-transparency (NEX-362 / M2) — per-PNR evaluation trace ──
 * The "innards" of the algorithm for ONE PNR group: each pipeline step, how many
 * candidates SURVIVED, and the BLOCKED candidates with their real machine reason
 * (transit visa / MCT / no-inventory …). Consumes the FROZEN operator-bff contract
 * `GET /v1/rebooking/{groupId}/evaluation-trace@v1`. The BFF degrades open: an
 * absent/empty trace returns `{ groupId, steps: [] }` (200) — NEVER an error — so
 * the timeline renders a graceful empty state, not a failure.
 */

/** One eliminated candidate + its `CODE: human detail` reason string. */
export interface IRebookingEvaluationTraceEliminated {
  candidate: string;
  reason: string;
}

/** One pipeline step in the per-PNR evaluation trace (frozen wire shape). */
export interface IRebookingEvaluationTraceStep {
  step: string;
  kept: number;
  eliminated: IRebookingEvaluationTraceEliminated[];
  urgencyScore?: number;
  rankingFactors?: Record<string, number>;
  policyParams?: Record<string, unknown>;
}

/** The per-PNR evaluation trace view (frozen wire shape). */
export interface IRebookingEvaluationTraceView {
  groupId: string;
  caseUrn?: string;
  steps: IRebookingEvaluationTraceStep[];
  capturedAt?: string;
}

/**
 * The per-PNR algorithm evaluation trace (NEX-362 / M2) — fetched once when a PNR
 * detail opens in the rebooking-progress dialog. Realtime is `none`: the trace is a
 * per-open snapshot, NOT a live feed, so this does exactly ONE HTTP read (staleTime
 * Infinity, no interval poll, no window-focus refetch) — never a poll. A
 * degrade-open empty body (`{ groupId, steps: [] }`, 200) is a normal success the
 * timeline renders as an empty state, not an error.
 */
export function useEvaluationTrace(groupId: string | undefined, enabled = true) {
  return useQuery<IRebookingEvaluationTraceView>({
    queryKey: ['disruptions', 'evaluation-trace', groupId],
    queryFn: () =>
      http
        .get(`v1/rebooking/${encodeURIComponent(groupId!)}/evaluation-trace`)
        .json<IRebookingEvaluationTraceView>(),
    enabled: !!groupId && enabled,
    // One-shot read on open; the trace is per-open (realtime none), never polled.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/* ── MASS ground transport ("Send to hotels") — transfer board hooks ─────────────── */

/** The query key of the disruption's transfer board — apply-event merges the WS
 *  `TransferRunChanged` / `TransferBoardChanged` pushes into THIS key. */
export function transferBoardKey(urn: string): readonly unknown[] {
  return ['disruptions', urn, 'transfers'] as const;
}

/**
 * The transfer board (plan + shuttle runs + per-hotel need). ONE HTTP read on open,
 * then WS-merged (setQueryData) by the disruption room — no interval poll. `null`
 * data = the BFF has no board for this disruption (older BFF / no transport).
 */
export function useTransferBoard(urn: string | undefined, enabled = true) {
  return useQuery<ITransferBoard | null>({
    queryKey: ['disruptions', urn, 'transfers'],
    queryFn: () => fetchTransferBoard(urn!),
    enabled: !!urn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

function afterTransferWrite(qc: ReturnType<typeof useQueryClient>, urn: string): void {
  void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'transfers'] });
  void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
  void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'progress'] });
}

/** Plan / re-cut the shuttle runs (all hotels, or one). Writes the returned board straight into the cache. */
export function usePlanTransfers(urn: string) {
  const qc = useQueryClient();
  return useMutation<ITransferBoard, Error, PlanTransfersInput | void>({
    mutationFn: (input) => planTransfers(urn, input ?? {}),
    onSuccess: (board) => {
      qc.setQueryData<ITransferBoard | null>(['disruptions', urn, 'transfers'], board);
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'resolution-state'] });
    },
  });
}

/** Dispatch the first wave (or a hotel's / the named runs). Live progress arrives over the WS. */
export function useDispatchTransfers(urn: string) {
  const qc = useQueryClient();
  return useMutation<DispatchTransfersResult, Error, DispatchTransfersInput>({
    mutationFn: (input) => dispatchTransfers(urn, input),
    onSuccess: () => afterTransferWrite(qc, urn),
  });
}

/** One action on one run (cancel · board · no-show · own-means · replace-no-shows). */
export function useTransferRunAction(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof transferRunAction>>,
    Error,
    { runUrn: string; action: TransferRunAction; reason?: string; passengerUrn?: string }
  >({
    mutationFn: ({ runUrn, action, reason, passengerUrn }) =>
      transferRunAction(runUrn, action, {
        ...(reason ? { reason } : {}),
        ...(passengerUrn ? { passengerUrn } : {}),
      }),
    onSuccess: () => afterTransferWrite(qc, urn),
  });
}

/* ── feat/care-desk K5 (NEX-457) — care board + care write hooks ─────────────── */

/** The query key of the disruption's care board — apply-event merges the WS
 *  `CareChanged` pushes into THIS key. */
export function careBoardKey(urn: string): readonly unknown[] {
  return ['disruptions', urn, 'care'] as const;
}

/**
 * The care board (per entitled PNR: card + balances + facts). ONE HTTP read on
 * open (seeded once — `staleTime: Infinity`), then WS-merged by `CareChanged`
 * (apply-event.ts patches the slice + invalidates for the full truth) — never an
 * interval poll. Degrade-open on the BFF; `known.wallet=false` means "balances
 * not read", which the UI must render honestly (never a fabricated $0.00).
 */
export function useCareBoard(urn: string | undefined, enabled = true) {
  return useQuery<CareBoard>({
    queryKey: ['disruptions', urn, 'care'],
    queryFn: () => fetchCareBoard(urn!),
    enabled: !!urn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The PNR card's history (care-card history, 2026-10-30). Keyed UNDER the care board's key
 * (`['disruptions', urn, 'care', 'history', sub]`), so the existing `CareChanged` push —
 * which invalidates the care prefix and re-reads the ACTIVE queries once, trailing — keeps
 * it live, and so does the end of a «Dar más comida» run. No poll.
 */
export function useCareHistory(
  urn: string | undefined,
  subCaseUrn: string | undefined,
  caseUrn: string | undefined,
  enabled = true,
) {
  return useCareQuery<CareCardHistory>(urn, ['history', subCaseUrn], () => fetchCareHistory(urn!, subCaseUrn!, caseUrn!), !!subCaseUrn && !!caseUrn && enabled);
}

/** A care read keyed under the care board's prefix — the CareChanged push re-reads it (no poll). */
function useCareQuery<T>(urn: string | undefined, tail: unknown[], fn: () => Promise<T | null>, enabled: boolean) {
  return useQuery<T | null>({
    queryKey: ['disruptions', urn, 'care', ...tail],
    queryFn: fn,
    enabled: !!urn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The disruption's card loads in batches (each meal tier, the allowance, each operator
 * grant). Same live path as {@link useCareHistory} (`['disruptions', urn, 'care', 'loads']`).
 */
export function useCareLoads(urn: string | undefined, enabled = true) {
  return useCareQuery<CareLoads>(urn, ['loads'], () => fetchCareLoads(urn!), enabled);
}

/** What the disruption gave its passengers — same live path as {@link useCareLoads}. */
export function useCareMoney(urn: string | undefined, enabled = true) {
  return useCareQuery<CareMoney>(urn, ['money'], () => fetchCareMoney(urn!), enabled);
}

/* ── transport-desk (slice T3, NEX-463) — disruption trips + dispatch-cars ──────── */

/** The query key of the disruption's CAR/on-demand trips — apply-event upserts the
 *  WS `TripChanged` push into THIS key when the trip carries its disruptionUrn. */
export function disruptionTripsKey(urn: string): readonly unknown[] {
  return ['disruptions', urn, 'trips'] as const;
}

/**
 * The disruption's CAR / on-demand trips (`GET /v1/disruptions/{urn}/trips`).
 * ONE HTTP read on open, then WS-merged (setQueryData) by the disruption room's
 * `TripChanged` pushes — no interval poll. `null` data = an older BFF without
 * the route (the cars strip renders "not wired").
 */
export function useDisruptionTrips(urn: string | undefined, enabled = true) {
  return useQuery<DisruptionTripsPage | null>({
    queryKey: ['disruptions', urn, 'trips'],
    queryFn: () => fetchDisruptionTrips(urn!),
    enabled: !!urn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * After a care write: the board must re-read (wallet truth) and the roster row's
 * cases denorm (careOutcome / mealVoucher chips) lands ASYNC over the bus — so
 * invalidate now AND once after 4 s (the documented denorm bridge; the WS
 * `CareChanged` push is what makes the second nudge usually redundant).
 */
function afterCareWrite(qc: ReturnType<typeof useQueryClient>, urn: string): void {
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'care'] });
    void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
  };
  invalidate();
  setTimeout(invalidate, 4000);
}

/**
 * Discretionary top-up of one group's live card (202 — settles async; the
 * `CareChanged` push flips the "settling" chip). K5 lands the hook; the K6
 * top-up dialog is its caller.
 */
export function useCareTopUp(urn: string) {
  const qc = useQueryClient();
  return useMutation<CareTopUpResult, Error, { subCaseUrn: string } & CareTopUpInput>({
    mutationFn: ({ subCaseUrn, ...body }) => postCareTopUp(urn, subCaseUrn, body),
    onSuccess: () => afterCareWrite(qc, urn),
  });
}

/** Void the group's prepaid card (K6 — supervisor-only, reason mandatory;
 *  irreversible for the group: the counter voucher is the only path afterward). */
export function useCareVoid(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    { voided: true; subCaseUrn: string },
    Error,
    { subCaseUrn: string; caseUrn?: string; reason: string }
  >({
    mutationFn: ({ subCaseUrn, caseUrn, reason }) =>
      postCareVoid(urn, subCaseUrn, { ...(caseUrn ? { caseUrn } : {}), reason }),
    onSuccess: () => afterCareWrite(qc, urn),
  });
}

/** Mark the group's benefit consumed (#808) — the wallet validates the printed
 *  reference; a 422/409 keeps the dialog open with the verdict. */
export function useCareRedeem(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    CareRedeemResult,
    Error,
    { subCaseUrn: string; caseUrn?: string; reference: string; amount?: number; note?: string }
  >({
    mutationFn: ({ subCaseUrn, caseUrn, reference, amount, note }) =>
      postCareRedeem(urn, subCaseUrn, {
        ...(caseUrn ? { caseUrn } : {}),
        reference,
        ...(typeof amount === 'number' ? { amount } : {}),
        ...(note ? { note } : {}),
      }),
    onSuccess: () => afterCareWrite(qc, urn),
  });
}

/** Counter lookup by printed coupon reference (#808) — fired on demand, never on keystroke. */
export function useCareLookup(urn: string) {
  return useMutation<CareLookupResult, Error, { reference: string }>({
    mutationFn: ({ reference }) => fetchCareLookup(urn, reference),
  });
}

/** Declare a terminal counter outcome (COUNTER_VOUCHER / HANDLED_OUTSIDE) for one
 *  PNR's care story — ends the group's card work on the desk. */
export function useDeclareCareOutcome(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    { declared: true; subCaseUrn: string; outcome: string },
    Error,
    { subCaseUrn: string; caseUrn: string; outcome: CareOutcomeKind; note?: string }
  >({
    mutationFn: ({ subCaseUrn, caseUrn, outcome, note }) =>
      postCareOutcome(urn, subCaseUrn, { caseUrn, outcome, ...(note ? { note } : {}) }),
    onSuccess: () => afterCareWrite(qc, urn),
  });
}

/** The below-threshold mass CAR fan-out (one car dispatch per group still owed a
 *  ride). The resulting `TripChanged` echoes land over the WS; the trips read is
 *  invalidated as a backstop for trips the push raced past. */
export function useDispatchCars(urn: string) {
  const qc = useQueryClient();
  return useMutation<DispatchCarsResult, Error, { reason: string }>({
    mutationFn: ({ reason }) => triggerDispatchCars(urn, reason),
    onSuccess: () => {
      afterTransferWrite(qc, urn);
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'trips'] });
    },
  });
}

/* ── transport-desk (slice T4, NEX-464) — run manifest + move-seats + add-run ──── */

/** The query key of ONE run's manifest — apply-event re-reads it when that run's
 *  own `TransferRunChanged` push lands (invalidate, not merge: the run event
 *  carries counters, not the seat list). */
export function runManifestKey(runUrn: string): readonly unknown[] {
  return ['transport', 'run', runUrn, 'manifest'] as const;
}

/**
 * The run sheet's manifest (`SeatView[]`, URNs only). ONE HTTP read on open;
 * freshness thereafter rides the run's `TransferRunChanged` pushes (apply-event
 * invalidates THIS key) — no interval poll, no window-focus refetch.
 */
export function useRunManifest(runUrn: string | undefined, enabled = true) {
  return useQuery<RunManifest>({
    queryKey: ['transport', 'run', runUrn, 'manifest'],
    queryFn: () => fetchRunManifest(runUrn!),
    enabled: !!runUrn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Move whole PNR clusters between runs (`POST /v1/transport/runs/{from}/move-seats`).
 * On success the board + both manifests + the roster walk are re-read so the run
 * sheet, the exceptions queue and the desk lanes advance without waiting on the
 * WS echo; 409 `run_departed`/`over_capacity` rethrows typed for the dialog.
 */
export function useMoveSeats(urn: string) {
  const qc = useQueryClient();
  return useMutation<MoveSeatsResult, Error, MoveSeatsInput & { runUrn: string }>({
    mutationFn: ({ runUrn, passengerUrns, toRunUrn, reason }) =>
      postMoveSeats(runUrn, { passengerUrns, toRunUrn, reason }),
    onSuccess: (_res, vars) => {
      afterTransferWrite(qc, urn);
      void qc.invalidateQueries({ queryKey: runManifestKey(vars.runUrn) });
      void qc.invalidateQueries({ queryKey: runManifestKey(vars.toRunUrn) });
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
    },
  });
}

/** Add one extra run for a hotel + leg (optionally dispatched immediately).
 *  404 `plan_not_found` rethrows typed — the dialog says "Plan shuttles first". */
export function useAddRun(urn: string) {
  const qc = useQueryClient();
  return useMutation<AddRunResult, Error, AddRunInput>({
    mutationFn: (input) => postAddRun(urn, input),
    onSuccess: () => afterTransferWrite(qc, urn),
  });
}

/** Declare the SEATLESS own-means outcome for one PNR (T2b seam). The roster
 *  walk is re-read so the desk classifier sees `transportOutcome` and the
 *  exceptions queue advances without waiting on the async denorm push. */
export function useTransportOutcome(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    TransportOutcomeResult,
    Error,
    TransportOutcomeInput & { subCaseUrn: string }
  >({
    mutationFn: ({ subCaseUrn, ...input }) => postTransportOutcome(urn, subCaseUrn, input),
    onSuccess: () => {
      afterTransferWrite(qc, urn);
      void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
    },
  });
}

/* ── transport-desk (slice T7, NEX-467) — the check-in loop ─────────────────────── */

/**
 * The `checkedInAt` denorm the Journeys view reads lands on the roster rows ASYNC
 * (cases K3 write → K5 denorm), so a check-in write invalidates the roster + trips
 * NOW and once more after a short delay — the same bounded one-shot bridge the
 * voucher flow uses (NOT a poll; the WS `CaseChanged(kind=passenger)` push is the
 * proper live path and collapses the duplicate).
 */
function checkinDenormBridge(qc: ReturnType<typeof useQueryClient>, urn: string): void {
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'roster'] });
    void qc.invalidateQueries({ queryKey: ['disruptions', urn, 'trips'] });
  };
  invalidate();
  setTimeout(invalidate, 4000);
}

/** Record ONE passenger as checked in (the operator fallback of the SMS loop). */
export function useOperatorCheckIn(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    OperatorCheckInResult,
    Error,
    { subCaseUrn: string; paxUrn: string; caseUrn: string; note?: string }
  >({
    mutationFn: ({ subCaseUrn, paxUrn, caseUrn, note }) =>
      postOperatorCheckIn(urn, subCaseUrn, paxUrn, { caseUrn, ...(note ? { note } : {}) }),
    onSuccess: () => checkinDenormBridge(qc, urn),
  });
}

/**
 * Set ONE passenger's adult / minor condition (#806). Resolving an UNKNOWN lifts
 * the party's `CONDITION_UNKNOWN` blocker, so the roster is re-read the same way
 * a check-in is (immediately, then once more after the denorm settles).
 */
export function usePassengerCondition(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    OperatorSetConditionResult,
    Error,
    { subCaseUrn: string; paxUrn: string; caseUrn: string; condition: PassengerCondition; note?: string }
  >({
    mutationFn: ({ subCaseUrn, paxUrn, caseUrn, condition, note }) =>
      postPassengerCondition(urn, subCaseUrn, paxUrn, { caseUrn, condition, ...(note ? { note } : {}) }),
    onSuccess: () => checkinDenormBridge(qc, urn),
  });
}

/** Link a minor to their responsible adult on another PNR (#806, test case 8b). */
export function useResponsibleAdultLink(urn: string) {
  const qc = useQueryClient();
  return useMutation<
    OperatorLinkResponsibleAdultResult,
    Error,
    { subCaseUrn: string; paxUrn: string; caseUrn: string; pnrLocator: string; paxUrnOfAdult?: string; name?: string }
  >({
    mutationFn: ({ subCaseUrn, paxUrn, caseUrn, pnrLocator, paxUrnOfAdult, name }) =>
      postResponsibleAdult(urn, subCaseUrn, paxUrn, {
        caseUrn,
        pnrLocator,
        ...(paxUrnOfAdult ? { paxUrn: paxUrnOfAdult } : {}),
        ...(name ? { name } : {}),
      }),
    onSuccess: () => checkinDenormBridge(qc, urn),
  });
}

/** Run the check-in reminder sweep now ([Send reminders]). The sends may flip
 *  reminder clocks the roster rows carry — same denorm bridge as the mark. */
export function useCheckinSweep(urn: string) {
  const qc = useQueryClient();
  return useMutation<CheckinReminderSweepResult, Error, void>({
    mutationFn: () => triggerCheckinSweep(urn),
    onSuccess: () => checkinDenormBridge(qc, urn),
  });
}
